'use client';

import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  RadioGroup,
  RadioGroupItem,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, CheckIcon, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import Breadcrumbs from '@Components/Breadcrumbs';
import ImageUploadForm from '@Components/ImageUploadForm';
import PhonePreviewContainer from '@Components/PhonePreviewContainer';
import { AspectRatio } from '@Components/AspectRatio';
import { Skeleton } from '@Components/Skeleton';
import EventPreview from '@Components/TicketedEvents/EventPreview';
import EventsSitePreview from '@Components/TicketedEvents/EventsSitePreview';
import { useEventPreviewStore } from '../../../stores/eventPreviewStore';
import Tiptap from '@Components/TipTap';
import { useAssets } from '@Hooks/useAssets';
import { useVenue } from '@Hooks/useVenue';
import service from '@Service';
import { cn } from '@Utils/cn';
import {
  dateTimeLocalToZoneISO,
  toISOorNull,
  toTrimmedOrNull,
} from '@Utils/utils';

import {
  VenuesComboboxInput,
  TagsMultiSelectInput,
} from '@Views/ticketed-events/create';

type EventVisibility = 'PUBLIC' | 'MEMBERS_ONLY';
type EventStatus = 'DRAFT' | 'PUBLISHED';

const FormSchema = z
  .object({
    name: z.string().min(2, 'Event name is required'),
    description: z.string().min(1, 'Description is required'),
    teaser: z.string().min(1, 'Teaser is required').max(200),
    startDateTime: z.string().min(1, 'Start date/time is required'),
    endDateTime: z.string().min(1, 'End date/time is required'),
    visibility: z.enum(['PUBLIC', 'MEMBERS_ONLY']),
    status: z.enum(['DRAFT', 'PUBLISHED']),
    venueId: z.string().min(1, 'Please select a venue'),
    tags: z.array(z.string()),
    featuredImageId: z.string().optional().nullable(),
    imageIds: z.array(z.string()),
  })
  .superRefine((val, ctx) => {
    const descStripped = (val.description ?? '').replace(/<[^>]*>/g, '').trim();
    if (descStripped.length < 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['description'],
        message: 'Description is required',
      });
    }

    const hasFeaturedImage =
      val.featuredImageId != null &&
      String(val.featuredImageId).trim().length > 0;
    if (!hasFeaturedImage) {
      ctx.addIssue({
        code: 'custom',
        path: ['featuredImageId'],
        message: 'Please select a featured image',
      });
    }

    const startRaw = val.startDateTime?.trim() ?? '';
    const endRaw = val.endDateTime?.trim() ?? '';
    if (!startRaw || !endRaw) return;
    const start = new Date(startRaw);
    const end = new Date(endRaw);
    if (Number.isNaN(start.getTime()))
      ctx.addIssue({
        code: 'custom',
        path: ['startDateTime'],
        message: 'Invalid start date/time',
      });
    if (Number.isNaN(end.getTime()))
      ctx.addIssue({
        code: 'custom',
        path: ['endDateTime'],
        message: 'Invalid end date/time',
      });
    if (
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      end <= start
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDateTime'],
        message: 'End must be after start',
      });
    }
  });

type FormValues = z.infer<typeof FormSchema>;

type CreateEventPayload = {
  event: {
    name: string;
    description: string;
    teaser?: string;
    startDateTime: string | null;
    endDateTime: string | null;
    visibility: EventVisibility;
    status: EventStatus;
  };
  venueId: string;
  featuredImageId?: string;
  imageIds?: string[];
};

type Step = 1 | 2;

export default function CreateEvent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [featuredImageSheetOpen, setFeaturedImageSheetOpen] = useState(false);
  const [gallerySheetOpen, setGallerySheetOpen] = useState(false);
  const [featuredImageDialogOpen, setFeaturedImageDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [featuredImageActiveTab, setFeaturedImageActiveTab] = useState<
    'mediaLibrary' | 'uploadImage'
  >('mediaLibrary');
  const [galleryActiveTab, setGalleryActiveTab] = useState<
    'mediaLibrary' | 'uploadImage'
  >('mediaLibrary');
  const [previewMode, setPreviewMode] = useState<'members-app' | 'events-site'>(
    'members-app',
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      description: '',
      teaser: '',
      startDateTime: '',
      endDateTime: '',
      visibility: 'PUBLIC',
      status: 'DRAFT',
      venueId: '',
      tags: [],
      featuredImageId: null,
      imageIds: [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    setFocus,
    formState: { isSubmitting },
  } = form;

  const featuredImageId = watch('featuredImageId');
  const imageIds = watch('imageIds') || [];
  const venueId = watch('venueId');

  const { data: assets, status: assetsStatus } = useAssets({
    enabled:
      featuredImageSheetOpen ||
      gallerySheetOpen ||
      featuredImageDialogOpen ||
      galleryDialogOpen ||
      !!featuredImageId ||
      (imageIds && imageIds.length > 0),
  });

  const { data: venue } = useVenue({
    venueId,
    toastInclude: 'toastRestaurant',
    enabled: !!venueId,
  });

  const venueTimeZone =
    venue?.toastRestaurant?.general?.timeZone ?? 'America/New_York';

  const raw = form.watch();

  const previewEvent = useMemo(() => {
    const featuredAsset = assets?.find((asset) => asset.id === featuredImageId);
    const galleryAssets =
      assets?.filter((asset) => imageIds.includes(asset.id)) || [];
    const startISO =
      dateTimeLocalToZoneISO(raw.startDateTime ?? '', venueTimeZone) ??
      toISOorNull(raw.startDateTime);
    const endISO =
      dateTimeLocalToZoneISO(raw.endDateTime ?? '', venueTimeZone) ??
      toISOorNull(raw.endDateTime);

    return {
      name: (raw.name || '').trim() || 'Untitled Event',
      description: raw.description || '',
      teaser: raw.teaser || null,
      startDateTime: startISO,
      endDateTime: endISO,
      capacity: 0,
      visibility: raw.visibility as EventVisibility,
      status: raw.status as EventStatus,
      featuredImage: featuredAsset
        ? {
            id: featuredAsset.id,
            cloudinaryId: featuredAsset.cloudinaryId,
          }
        : undefined,
      images: galleryAssets.map((asset) => ({
        id: asset.id,
        cloudinaryId: asset.cloudinaryId,
      })),
      ticketTypes: [],
    };
  }, [raw, assets, featuredImageId, imageIds, venueTimeZone]);

  const step1Fields: (keyof FormValues)[] = [
    'name',
    'description',
    'teaser',
    'startDateTime',
    'endDateTime',
    'visibility',
    'status',
    'venueId',
    'tags',
  ];

  const validateStep = async (step: Step): Promise<boolean> => {
    let fieldsToValidate: (keyof FormValues)[] = [];

    switch (step) {
      case 1:
        fieldsToValidate = step1Fields;
        break;
      case 2:
        fieldsToValidate = ['featuredImageId'];
        break;
    }

    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid && fieldsToValidate.length > 0) {
      setFocus(fieldsToValidate[0] as any);
    }
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (!isValid) return;
    if (currentStep < 2) {
      setCurrentStep((prev) => (prev + 1) as Step);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as Step);
    }
  };

  const onSubmit = async (values: FormValues) => {
    const startISO =
      dateTimeLocalToZoneISO(values.startDateTime ?? '', venueTimeZone) ??
      toISOorNull(values.startDateTime);
    const endISO =
      dateTimeLocalToZoneISO(values.endDateTime ?? '', venueTimeZone) ??
      toISOorNull(values.endDateTime);

    const trimmedTeaser = toTrimmedOrNull(values.teaser);
    const descriptionStr = toTrimmedOrNull(values.description) ?? '';

    const payload: CreateEventPayload = {
      event: {
        name: values.name.trim(),
        description: descriptionStr,
        ...(trimmedTeaser ? { teaser: trimmedTeaser } : {}),
        startDateTime: startISO,
        endDateTime: endISO,
        visibility: values.visibility as EventVisibility,
        status: values.status as EventStatus,
      },
      venueId: values.venueId,
      ...(values.featuredImageId
        ? { featuredImageId: values.featuredImageId }
        : {}),
      imageIds:
        values.imageIds && values.imageIds.length > 0
          ? values.imageIds
          : undefined,
    };

    try {
      const response = await service.createEvent(payload);

      if (!response.ok) {
        throw new Error('Failed to create event');
      }

      const event = await response.json();

      const eventId =
        event?.data?.id || event?.id || event?.data?.attributes?.id;

      toastSuccess('Event created successfully.');

      if (eventId) {
        navigate(`/events/${eventId}`);
      } else {
        console.warn('Event created but ID not found in response:', event);
        navigate('/events/general');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  };

  return (
    <div>
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'General', href: '/events/general' },
          { name: 'Create an Event', href: '/events/create' },
        ]}
      />
      <div className="flex">
        <div className="w-2/5">
          <div className="sticky top-0">
            <div className="my-4 flex items-end">
              <Heading>Create an Event</Heading>
            </div>

            <Form {...form}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Card className="border-zinc-200">
                  <CardContent className="space-y-8">
                    <div className="flex flex-col gap-16">
                      {/* Step 1: Basic Details */}
                      <div className="flex flex-col gap-6">
                        <div className="flex items-center gap-3">
                          <Heading
                            level="h2"
                            className="flex-1 text-2xl font-semibold"
                          >
                            Basic Details
                          </Heading>
                          {currentStep > 1 && (
                            <CheckCircle className="h-7 w-7 shrink-0 text-[#CAAB6A]" />
                          )}
                        </div>
                        {currentStep === 1 && (
                          <div className="space-y-6">
                            <FormField
                              control={control}
                              name="name"
                              render={({ field }) => (
                                <FormItem className="lg:col-span-2">
                                  <FormLabel>Event name</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="Wine & Cheese Evening"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Description</FormLabel>
                                  <FormControl>
                                    <Tiptap
                                      value={field.value || ''}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                      placeholder="Describe your event..."
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="teaser"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Teaser</FormLabel>
                                  <FormControl>
                                    <Textarea
                                      placeholder="A short preview of the event."
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="venueId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Venue</FormLabel>
                                  <FormControl>
                                    <VenuesComboboxInput
                                      value={field.value}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                      placeholder="Select a venue..."
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                              <FormField
                                control={control}
                                name="startDateTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Start</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="datetime-local"
                                        value={field.value}
                                        onChange={(event) =>
                                          field.onChange(event.target.value)
                                        }
                                        onBlur={field.onBlur}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={control}
                                name="endDateTime"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>End</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="datetime-local"
                                        value={field.value}
                                        onChange={(event) =>
                                          field.onChange(event.target.value)
                                        }
                                        onBlur={field.onBlur}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={control}
                              name="visibility"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Visibility</FormLabel>
                                  <FormControl>
                                    <RadioGroup
                                      onValueChange={field.onChange}
                                      value={field.value}
                                      className="flex flex-col gap-2"
                                    >
                                      <FormItem className="flex items-center space-y-0 space-x-2">
                                        <FormControl>
                                          <RadioGroupItem
                                            id="visibility-public"
                                            value="PUBLIC"
                                          />
                                        </FormControl>
                                        <FormLabel
                                          htmlFor="visibility-public"
                                          className="font-normal"
                                        >
                                          Public
                                        </FormLabel>
                                      </FormItem>

                                      <FormItem className="flex items-center space-y-0 space-x-2">
                                        <FormControl>
                                          <RadioGroupItem
                                            id="visibility-members"
                                            value="MEMBERS_ONLY"
                                          />
                                        </FormControl>
                                        <FormLabel
                                          htmlFor="visibility-members"
                                          className="font-normal"
                                        >
                                          Members only
                                        </FormLabel>
                                      </FormItem>
                                    </RadioGroup>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="status"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Status</FormLabel>
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                  >
                                    <FormControl>
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select status" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="DRAFT">
                                        Draft
                                      </SelectItem>
                                      <SelectItem value="PUBLISHED">
                                        Published
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name="tags"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Tags</FormLabel>
                                  <FormControl>
                                    <TagsMultiSelectInput
                                      value={field.value ?? []}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                      placeholder="Select tags..."
                                    />
                                  </FormControl>
                                  <FormDescription>
                                    Select one or more tags.
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Step 2: Media */}
                    <div className="flex flex-col gap-6">
                      <div className="flex items-center gap-3">
                        <Heading
                          level="h2"
                          className="flex-1 text-2xl font-semibold"
                        >
                          Media
                        </Heading>
                      </div>
                      {currentStep === 2 && (
                        <div className="space-y-6">
                          <div className="space-y-1.5">
                            <FormLabel>Featured image</FormLabel>
                            <FormField
                              control={control}
                              name="featuredImageId"
                              render={() => {
                                const featuredAsset = assets?.find(
                                  (asset) => asset.id === featuredImageId,
                                );

                                return (
                                  <FormItem>
                                    <FormControl>
                                      <div className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-4">
                                        <div className="flex h-[216px] items-center justify-center overflow-hidden rounded">
                                          {featuredAsset ? (
                                            <CloudinaryImage
                                              publicId={
                                                featuredAsset.cloudinaryId
                                              }
                                              mode="original"
                                              className="h-full w-full object-cover"
                                            />
                                          ) : (
                                            <div className="flex h-full w-full items-center justify-center bg-zinc-200 text-sm text-zinc-500">
                                              No featured image selected
                                            </div>
                                          )}
                                        </div>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="w-fit self-center border-zinc-300 bg-white"
                                          onClick={() => {
                                            setFeaturedImageSheetOpen(true);
                                          }}
                                        >
                                          Change
                                        </Button>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                );
                              }}
                            />
                          </div>

                          <div className="space-y-1.5">
                            <FormLabel>Image/Video Gallery</FormLabel>
                            <FormField
                              control={control}
                              name="imageIds"
                              render={({ field }) => {
                                const selectedAssets = assets?.filter((asset) =>
                                  (field.value || []).includes(asset.id),
                                );

                                return (
                                  <FormItem>
                                    <FormControl>
                                      <div className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-4">
                                        {selectedAssets &&
                                        selectedAssets.length > 0 ? (
                                          <div className="flex flex-wrap gap-4">
                                            {selectedAssets.map((asset) => (
                                              <div
                                                key={asset.id}
                                                className="h-32 w-36 overflow-hidden rounded"
                                              >
                                                <CloudinaryImage
                                                  publicId={asset.cloudinaryId}
                                                  mode="original"
                                                  className="h-full w-full object-cover"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="flex h-32 items-center justify-center text-sm text-zinc-500">
                                            No gallery images selected
                                          </div>
                                        )}
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="w-fit self-center border-zinc-300 bg-white"
                                          onClick={() => {
                                            setGallerySheetOpen(true);
                                          }}
                                        >
                                          Manage gallery images
                                        </Button>
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                );
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Navigation Buttons */}
                    {currentStep === 1 && (
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleNext}
                          className="bg-zinc-200 hover:bg-zinc-300"
                        >
                          Next
                        </Button>
                      </div>
                    )}

                    {currentStep === 2 && (
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleBack}
                          className="bg-zinc-200 hover:bg-zinc-300"
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          loading={isSubmitting}
                          className="bg-[#CAAB6A] hover:bg-[#B89A5A]"
                        >
                          Create Event
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </form>
            </Form>
          </div>
        </div>
        <div className="w-3/5">
          <section className="sticky top-8 space-y-4 p-4">
            <div className="flex flex-col space-y-8 xl:col-span-2">
              <div className="sticky top-4">
                <div className="mb-6 text-center">
                  <Heading level="h3">Preview</Heading>

                  <div className="mx-auto mt-2 flex max-w-xs items-stretch gap-1 rounded-lg bg-zinc-100 p-1">
                    <Button
                      type="button"
                      variant={
                        previewMode === 'members-app' ? 'secondary' : 'ghost'
                      }
                      className={cn(
                        'flex-1 hover:bg-white',
                        previewMode === 'members-app' && 'bg-white shadow-md',
                      )}
                      onClick={() => setPreviewMode('members-app')}
                      size="sm"
                    >
                      Members App
                    </Button>
                    <Button
                      type="button"
                      variant={
                        previewMode === 'events-site' ? 'secondary' : 'ghost'
                      }
                      className={cn(
                        'flex-1 hover:bg-white',
                        previewMode === 'events-site' && 'bg-white shadow-md',
                      )}
                      onClick={() => setPreviewMode('events-site')}
                      size="sm"
                    >
                      Events Site
                    </Button>
                  </div>
                </div>
                <PhonePreviewContainer>
                  {previewMode === 'members-app' ? (
                    <EventPreview event={previewEvent as any} venue={venue} />
                  ) : (
                    <EventsSitePreview
                      event={previewEvent as any}
                      venue={venue}
                    />
                  )}
                </PhonePreviewContainer>
                {/* Preview in new tab button */}
                {previewMode === 'events-site' && (
                  <div className="z-10 mt-8 mb-2 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        useEventPreviewStore.getState().setPreviewData({
                          event: previewEvent as any,
                          venue,
                        });
                        window.open(
                          '/ticketed-events/preview',
                          'event-preview',
                        );
                      }}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Preview in new tab
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Featured Image Selection Sheet */}
      <Sheet
        open={featuredImageSheetOpen}
        onOpenChange={setFeaturedImageSheetOpen}
      >
        <SheetContent
          onPointerDownOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>Select featured image</SheetTitle>
            </SheetHeader>

            <SheetBody className="grow overflow-auto">
              <div className="space-y-6">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium">Featured Image</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFeaturedImageDialogOpen(true)}
                    >
                      {featuredImageId ? 'Change' : 'Select'} featured image
                    </Button>
                  </div>
                  {featuredImageId ? (
                    (() => {
                      const featuredAsset = assets?.find(
                        (asset) => asset.id === featuredImageId,
                      );
                      return featuredAsset ? (
                        <div className="flex min-h-42 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                          <CloudinaryImage
                            publicId={featuredAsset.cloudinaryId}
                            mode="original"
                            className="h-64 w-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                          <div className="text-sm text-zinc-500">
                            Loading image...
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                      <div className="text-sm text-zinc-500">
                        No featured image selected
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Dialog
                open={featuredImageDialogOpen}
                onOpenChange={setFeaturedImageDialogOpen}
              >
                <DialogContent className="sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Select featured image</DialogTitle>
                  </DialogHeader>

                  <Tabs
                    value={featuredImageActiveTab}
                    onValueChange={(val) =>
                      setFeaturedImageActiveTab(
                        val as 'mediaLibrary' | 'uploadImage',
                      )
                    }
                    className="w-full"
                  >
                    <TabsList>
                      <TabsTrigger value="mediaLibrary">
                        Media Library
                      </TabsTrigger>
                      <TabsTrigger value="uploadImage">Upload</TabsTrigger>
                    </TabsList>

                    <TabsContent value="mediaLibrary" className="mt-4">
                      <div className="grid max-h-128 grid-cols-3 gap-4 overflow-y-auto p-1">
                        {assetsStatus === 'pending' ? (
                          <>
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                          </>
                        ) : !assets || assets.length === 0 ? (
                          <div className="col-span-3 flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                            <div className="font-medium text-zinc-700">
                              No uploaded images
                            </div>
                          </div>
                        ) : (
                          assets.map((asset) => {
                            const selected = featuredImageId === asset.id;
                            const tagNames =
                              asset.tags?.map((t: any) => t.tag.name) ?? [];
                            return (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => {
                                  setValue(
                                    'featuredImageId',
                                    selected ? null : asset.id,
                                  );
                                  setFeaturedImageDialogOpen(false);
                                }}
                                className={cn(
                                  'relative flex w-full flex-col overflow-hidden rounded-md border bg-zinc-200 transition',
                                  selected
                                    ? 'border-primary ring-4 ring-[#CAAB6A]'
                                    : 'border-transparent hover:border-zinc-300',
                                )}
                              >
                                <AspectRatio ratio={1} className="relative w-full">
                                  <CloudinaryImage
                                    publicId={asset.cloudinaryId}
                                    mode="original"
                                    className="h-full w-full object-contain"
                                  />
                                  {selected && (
                                    <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                      <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                    </div>
                                  )}
                                </AspectRatio>
                                {tagNames.length > 0 && (
                                  <div className="flex flex-wrap gap-1 bg-white px-1.5 py-1.5">
                                    {tagNames.map((name: string) => (
                                      <span
                                        key={name}
                                        className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                                      >
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="uploadImage" className="mt-4">
                      <ImageUploadForm
                        inputId="event-featured-image-upload-input"
                        onUploadSuccess={() => {
                          queryClient.invalidateQueries({
                            queryKey: ['assets'],
                          });
                        }}
                      />
                    </TabsContent>
                  </Tabs>

                  {featuredImageActiveTab === 'mediaLibrary' && (
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="link">
                          Cancel
                        </Button>
                      </DialogClose>
                    </DialogFooter>
                  )}
                </DialogContent>
              </Dialog>
            </SheetBody>

            <SheetFooter>
              <Button
                variant="secondary"
                onClick={() => setFeaturedImageSheetOpen(false)}
              >
                Done
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      {/* Gallery Images Selection Sheet */}
      <Sheet open={gallerySheetOpen} onOpenChange={setGallerySheetOpen}>
        <SheetContent
          onPointerDownOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>Manage gallery images</SheetTitle>
            </SheetHeader>

            <SheetBody className="grow overflow-auto">
              <div className="space-y-6">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium">Gallery Images</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGalleryDialogOpen(true)}
                    >
                      Manage images
                    </Button>
                  </div>
                  {imageIds && imageIds.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {imageIds.map((imageId) => {
                        const asset = assets?.find(
                          (assetItem) => assetItem.id === imageId,
                        );
                        return (
                          <div
                            key={imageId}
                            className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
                          >
                            {asset ? (
                              <CloudinaryImage
                                publicId={asset.cloudinaryId}
                                mode="original"
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-xs text-zinc-500">
                                Loading...
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 py-8 text-center">
                      <div className="text-sm text-zinc-500">
                        No gallery images selected
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Dialog
                open={galleryDialogOpen}
                onOpenChange={setGalleryDialogOpen}
              >
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Select gallery images</DialogTitle>
                  </DialogHeader>

                  <Tabs
                    value={galleryActiveTab}
                    onValueChange={(val) =>
                      setGalleryActiveTab(val as 'mediaLibrary' | 'uploadImage')
                    }
                    className="w-full"
                  >
                    <TabsList>
                      <TabsTrigger value="mediaLibrary">
                        Media Library
                      </TabsTrigger>
                      <TabsTrigger value="uploadImage">Upload</TabsTrigger>
                    </TabsList>

                    <TabsContent value="mediaLibrary" className="mt-4">
                      <div className="grid max-h-128 grid-cols-3 gap-4 overflow-y-auto p-1">
                        {assetsStatus === 'pending' ? (
                          <>
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="aspect-square w-full" />
                          </>
                        ) : !assets || assets.length === 0 ? (
                          <div className="col-span-3 flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                            <div className="font-medium text-zinc-700">
                              No uploaded images
                            </div>
                          </div>
                        ) : (
                          assets.map((asset) => {
                            const selected = imageIds.includes(asset.id);
                            const tagNames =
                              asset.tags?.map((t: any) => t.tag.name) ?? [];
                            return (
                              <button
                                key={asset.id}
                                type="button"
                                onClick={() => {
                                  const currentIds = imageIds || [];
                                  const newIds = selected
                                    ? currentIds.filter((id) => id !== asset.id)
                                    : [...currentIds, asset.id];
                                  setValue('imageIds', newIds);
                                }}
                                className={cn(
                                  'relative flex w-full flex-col overflow-hidden rounded-md border bg-zinc-200 transition',
                                  selected
                                    ? 'border-primary ring-4 ring-[#CAAB6A]'
                                    : 'border-transparent hover:border-zinc-300',
                                )}
                              >
                                <AspectRatio ratio={1} className="relative w-full">
                                  <CloudinaryImage
                                    publicId={asset.cloudinaryId}
                                    mode="original"
                                    className="h-full w-full object-contain"
                                  />
                                  {selected && (
                                    <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                      <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                    </div>
                                  )}
                                </AspectRatio>
                                {tagNames.length > 0 && (
                                  <div className="flex flex-wrap gap-1 bg-white px-1.5 py-1.5">
                                    {tagNames.map((name: string) => (
                                      <span
                                        key={name}
                                        className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                                      >
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="uploadImage" className="mt-4">
                      <ImageUploadForm
                        inputId="event-gallery-image-upload-input"
                        onUploadSuccess={() => {
                          queryClient.invalidateQueries({
                            queryKey: ['assets'],
                          });
                        }}
                      />
                    </TabsContent>
                  </Tabs>

                  {galleryActiveTab === 'mediaLibrary' && (
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="link">
                          Cancel
                        </Button>
                      </DialogClose>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => {
                          setGalleryDialogOpen(false);
                        }}
                      >
                        Save
                      </Button>
                    </DialogFooter>
                  )}
                </DialogContent>
              </Dialog>
            </SheetBody>

            <SheetFooter>
              <Button
                variant="secondary"
                onClick={() => setGallerySheetOpen(false)}
              >
                Done
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
